import {
    ATTRIBUTES,
    openBackInStockModal,
    PUB_SUB_EVENTS,
    SELECTORS,
    createATCitems
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { publish, getQuickAddPopup } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
    addItemsToCart,
    getCartAttributesFromFormData,
    getDOMCartSectionApiIds,
    renderResponseToCartDrawer
} from '@/scripts/core/cart-functions';
import { applyStoqPreorderProperties } from '@/scripts/product/stoq-selling-plan'
import { SizeGuide } from './size-guide';

export class QuickAddPopup extends UcoastEl {
    static htmlSelector = 'quick-add-popup'
    static selectors = {
        form: 'form',
        submitButton: '[type="submit"]',
        colorInputs: '[name="quick-add-popup-color-input"]',
        selectedColorEl: '.quick-add-popup__color-selected',
        genderInputs: '[name="quick-add-popup-gender"]',
        sizeButtons: '.quick-add-popup__size-button',
        sizeChartToggle: '.quick-add-popup__size-chart',
        popupContainer: '.quick-add-popup-container',
        popupContent: '.quick-add-popup-content',
        formIdEl: '[name="id"]'
    }

    form: HTMLFormElement
    formIdEl: HTMLInputElement
    colorInputs?: NodeListOf<HTMLInputElement> 
    selectedColorEl?: HTMLElement
    genderInputs: NodeListOf<HTMLInputElement>
    sizeButtons: NodeListOf<HTMLButtonElement>
    sizeChartToggle?: HTMLButtonElement
    popupContainer: HTMLElement
    popupContent: HTMLElement
    hideErrors: boolean
    error?: boolean

    constructor() {
        super()

        this.form = q.rs(QuickAddPopup.selectors.form, this)
        this.formIdEl = (this.form.querySelector('[name="id"]') as HTMLInputElement) || (this.form.querySelector('[name="items[0][id]"]') as HTMLInputElement)
        this.genderInputs = q.rl(QuickAddPopup.selectors.genderInputs, this)
        this.sizeButtons = q.rl(QuickAddPopup.selectors.sizeButtons, this)
        this.sizeChartToggle = q.os(QuickAddPopup.selectors.sizeChartToggle, this)
        this.popupContainer = q.rs(QuickAddPopup.selectors.popupContainer, document)
        this.popupContent = q.rs(QuickAddPopup.selectors.popupContent, document)
        this.selectedColorEl = this.querySelector(QuickAddPopup.selectors.selectedColorEl) as HTMLElement
        this.colorInputs = this.querySelectorAll(QuickAddPopup.selectors.colorInputs)

        this.colorInputs?.forEach(input => {
            input.addEventListener('change', this.onChangeColor.bind(this))
        })
        this.genderInputs.forEach(input => {
            input.addEventListener('change', this.onChangeGender.bind(this))
        })
        this.sizeButtons.forEach(button => {
            button.addEventListener('click', this.onSubmitHandler.bind(this))
        })
        this.sizeChartToggle?.addEventListener('click', (e: Event) => {
            e.preventDefault()
            const sizeGuide = document.querySelector('size-guide') as SizeGuide
            if(sizeGuide) sizeGuide.open()
        })

        this.hideErrors = this.dataset.hideErrors === 'true'
    }

    async onChangeColor(e: Event) {
        e.preventDefault()
        const selectedColorInput = q.rs('[name="quick-add-popup-color-input"]:checked', this) as HTMLInputElement
        if(!selectedColorInput) return
        const selectedColor = selectedColorInput.value
        if (this.selectedColorEl) this.selectedColorEl.textContent = selectedColor
        const url = selectedColorInput.dataset.url
        if(!url) return
        const popup = await getQuickAddPopup(url)
        this.popupContent.innerHTML = popup.outerHTML
    }

    onChangeGender(e: Event) {
        e.preventDefault()
        const selectedGenderInput = q.rs('[name="quick-add-popup-gender"]:checked', this) as HTMLInputElement
        if(!selectedGenderInput) return

        const selectedGender = selectedGenderInput.value
        q.rl('.quick-add-popup-size__label-text[data-gender]', this).forEach(text => {
            text.classList.toggle('hidden', text.dataset.gender != selectedGender)
        })
    }

    getSpinner(target: HTMLButtonElement) {
        return q.rs(SELECTORS.loadingOverlaySpinner, target)
    }

    onSubmitHandler(event: Event) {
        event.preventDefault()
        const target = event.currentTarget as HTMLButtonElement
        if(!target) return
        const variantId = target.dataset.variantId
        if(!variantId) return
        this.formIdEl.value = variantId
        const bisVariantId = q.oa(target, 'data-uc-bis-variant-id')
        const oosUrl = q.oa(target, 'data-oos-popup-trigger')
        if (bisVariantId) {
            openBackInStockModal({
                opener: target,
                klaviyoUrl: oosUrl,
                variantId: bisVariantId,
            })
            return
        }
        const preorder = target.dataset.preorder == 'true'

        target.setAttribute('aria-disabled', 'true')
        target.setAttribute(ATTRIBUTES.loading, '')
        this.getSpinner(target).classList.remove('hidden')

        const formData = new FormData(this.form)
        if (formData.has('quick-add-popup-gender')) {
            formData.delete('quick-add-popup-gender')
        }
        let formVariantId = formData.get('id') || formData.get('items[0][id]')
        if (!formVariantId) throw Error('No variant id found')
        const addedVariantId = parseInt(formVariantId.toString())

        type AddToCartItem = {
            id: number
            quantity: number
            selling_plan?: number
            properties: Record<string, string>
            parent_id?: number
        }
        let items: AddToCartItem[] = []
        if(formData.get('items[0][id]')) {
            applyStoqPreorderProperties({
                formData,
                preorder,
            })
            items = createATCitems(formData) as AddToCartItem[]
        } else {
            applyStoqPreorderProperties({
                formData,
                preorder,
            })
        }
        const cartAttributes = getCartAttributesFromFormData(formData)

        addItemsToCart(
            items.length ? items : formData,
            getDOMCartSectionApiIds(),
            cartAttributes
        )
            .then((cart) => {
                if ('status' in cart) {
                    return
                }

                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: addedVariantId,
                })
                renderResponseToCartDrawer(cart)
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {
                target.removeAttribute(ATTRIBUTES.loading)

                if (!this.error)
                    target.removeAttribute('aria-disabled')
                this.getSpinner(target).classList.add('hidden')
            })
    }
}

export class QuickAddToggle extends UcoastEl {
    static htmlSelector = 'quick-add-toggle'
    toggleButton: HTMLButtonElement
    popupContainer: HTMLElement
    popupContent: HTMLElement

    constructor() {
        super()
        this.toggleButton = q.rs('button', this)
        this.popupContainer = q.rs('.quick-add-popup-container', document)
        this.popupContent = q.rs('.quick-add-popup-content', document)
        this.toggleButton.addEventListener('click', this.openPopup.bind(this))
    }

    async openPopup() {
        const url = this.dataset.url
        if(!url) return
        const popup = await getQuickAddPopup(url)
        this.popupContent.innerHTML = popup.outerHTML
        this.popupContainer.classList.add('active')
    }
}

export class QuickAddPopupClose extends UcoastEl {
    static htmlSelector = 'quick-add-popup-close'
    closeButton: HTMLButtonElement
    popupContainer: HTMLElement
    popupContent: HTMLElement

    constructor() {
        super()
        this.closeButton = q.rs('button', this)
        this.popupContainer = q.rs('.quick-add-popup-container', document)
        this.popupContent = q.rs('.quick-add-popup-content', document)
        this.closeButton.addEventListener('click', this.closePopup.bind(this))
    }

    closePopup() {
        this.popupContent.innerHTML = ''
        this.popupContainer.classList.remove('active')
    }
}
